import { apiInstance, captureAdminApiError } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const SUBMIT_ORDER_URL = 'backoffice/order/submit';

interface SubmitOrderPayload {
  id: string;
}

interface SubmitOrderResponse {
  success: boolean;
  message: string;
}

const submitOrder = async (
  token: string,
  payload: SubmitOrderPayload
): Promise<SubmitOrderResponse> => {
  const response = await apiInstance(token).patch(
    `${SUBMIT_ORDER_URL}/${payload.id}`,
    JSON.stringify({ id: payload.id })
  );
  return response.data;
};

export const handleSubmitOrderError = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const errorCode = error.response?.data?.error;
    if (typeof errorCode === 'string' && errorCode.startsWith('SAP_')) {
      return error.response?.data?.message || errorCode;
    }
    return (
      error.response?.data?.message || 'Terjadi kesalahan saat submit order'
    );
  }
  return 'Terjadi kesalahan yang tidak diketahui';
};

const captureListSubmitError = (options: {
  error: unknown;
  orderId: string;
  operation: 'submit_order_list' | 'submit_order_list_bulk';
  userId?: string;
  userEmail?: string;
}) => {
  captureAdminApiError({
    error: options.error,
    requestPath: `${SUBMIT_ORDER_URL}/${options.orderId}`,
    httpMethod: 'PATCH',
    queryKey: 'submit-order-list',
    operation: options.operation,
    feature: 'activity',
    extras: { orderId: options.orderId },
    user: {
      id: options.userId,
      email: options.userEmail,
    },
    level: 'error',
  });
};

export const useActivityMutations = () => {
  const session = useAuthStore();
  const token = session.access_token;
  const queryClient = useQueryClient();

  const submitOrderMutation = useMutation({
    mutationFn: (payload: SubmitOrderPayload) => submitOrder(token, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Activities'] });
    },
    onError: (error, payload) => {
      captureListSubmitError({
        error,
        orderId: payload.id,
        operation: 'submit_order_list',
        userId: session.userId,
        userEmail: session.userEmail,
      });
    },
  });

  const submitMultipleOrdersMutation = useMutation({
    mutationFn: async (payloads: SubmitOrderPayload[]) => {
      const results = await Promise.allSettled(
        payloads.map((payload) => submitOrder(token, payload))
      );

      const successful = results.filter(
        (result): result is PromiseFulfilledResult<SubmitOrderResponse> =>
          result.status === 'fulfilled'
      );

      const failed = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      );

      results.forEach((result, index) => {
        if (result.status !== 'rejected') {
          return;
        }

        const orderId = payloads[index]?.id;
        if (!orderId) {
          return;
        }

        captureListSubmitError({
          error: result.reason,
          orderId,
          operation: 'submit_order_list_bulk',
          userId: session.userId,
          userEmail: session.userEmail,
        });
      });

      return {
        successful: successful.length,
        failed: failed.length,
        total: payloads.length,
        errors: failed.map((result) => handleSubmitOrderError(result.reason)),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Activities'] });
    },
  });

  return {
    submitOrder: submitOrderMutation.mutateAsync,
    submitMultipleOrders: submitMultipleOrdersMutation.mutateAsync,
    isSubmitting: submitOrderMutation.isPending,
    isSubmittingMultiple: submitMultipleOrdersMutation.isPending,
    submitError: submitOrderMutation.error,
    submitMultipleError: submitMultipleOrdersMutation.error,
  };
};
